import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import './Header.css';
import AvatarMenu from './AvatarMenu';
import '../styles/theme.css';
import MobileMenu from './MobileMenu';
import { useFavorites, openFavoritesDrawer } from '../hooks/useFavorites';
import { pb } from '../lib/pb';
import { fetchSettings } from '../lib/settings';
import { FacebookIcon, InstagramIcon, LinkedinIcon } from './icons';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count } = useFavorites();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sProds, setSProds] = useState([]);
  const [sProjs, setSProjs] = useState([]);
  const [settings, setSettings] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const submitSearch = () => {
    const q = (query || '').trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  // Debounced search suggestions
  useEffect(() => {
    let alive = true;
    const q = (query || '').trim();
    if (q.length < 2) {
      setSProds([]); setSProjs([]); setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const qp = q.replaceAll('"', '\\"');
        const prodFilter = `published=true && (title ~ "%${qp}%" || slug ~ "%${qp}%")`;
        const projFilter = `published=true && (title ~ "%${qp}%" || slug ~ "%${qp}%" || client ~ "%${qp}%" || location ~ "%${qp}%")`;
        const [prods, projs] = await Promise.all([
          pb.collection('products').getList(1, 5, { filter: prodFilter, sort: 'order,title' }),
          pb.collection('projects').getList(1, 5, { filter: projFilter, sort: '-date,order,title' })
        ]);
        if (!alive) return;
        setSProds(prods.items || []);
        setSProjs(projs.items || []);
        setLoading(false);
        setOpen(true);
      } catch {
        if (!alive) return;
        setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const onDocDown = (e) => {
      if (!boxRef.current) return;
      if (boxRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => { });
  }, []);

  const resolve = (obj, keys = []) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };

  const ensureHttp = (url) => {
    if (!url) return '';
    return /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
  };

  const fbUrl = ensureHttp((settings?.socials?.facebook) || resolve(settings, ['facebook', 'facebook_url', 'fb', 'facebookLink', 'url_facebook', 'reseaux_facebook']));
  const igUrl = ensureHttp((settings?.socials?.instagram) || resolve(settings, ['instagram', 'instagram_url', 'ig', 'instagramLink', 'url_instagram', 'reseaux_instagram']));
  const lnUrl = ensureHttp((settings?.socials?.linkedin) || resolve(settings, ['linkedin', 'linkedin_url', 'ln', 'linkedinLink', 'url_linkedin', 'reseaux_linkedin']));

  return (
    <header className="header">
      <div className="container topbar">
        <div className="topbar-left">
          <button className="hamburger" aria-label="Menu" onClick={() => setMobileOpen(true)}>
            <span />
            <span />
            <span />
          </button>
          <span
            className="hamburger-label"
            onClick={() => setMobileOpen(true)}
          >
            Menu
          </span>
        </div>
        <div className="brand">
          <img src="/logo192.png" alt="AEF32" />
          <div className="brand-text">
            <div style={{ fontSize: 18, letterSpacing: 0.5 }}>AEF32</div>
            <div className="brand-subtitle" style={{ fontSize: 12, color: 'var(--muted)' }}>Portails & Automatismes</div>
          </div>
        </div>
        <div className="search" ref={boxRef} style={{ position: 'relative' }}>
          <input
            placeholder="Rechercher un produit ou un projet"
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => { if ((query || '').trim().length >= 2) setOpen(true); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setOpen(false); submitSearch(); } }}
          />
          <button
            type="button"
            aria-label="Lancer la recherche"
            onClick={submitSearch}
            style={{ marginLeft: 6, border: '1px solid var(--border)', background: '#fff', borderRadius: 8, padding: '6px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>
          {open && (
            <div
              role="listbox"
              style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
                boxShadow: '0 6px 22px rgba(0,0,0,0.08)', padding: 8, zIndex: 60
              }}
            >
              {loading && <div className="muted" style={{ padding: 8 }}>Recherche…</div>}
              {!loading && (sProds.length === 0 && sProjs.length === 0) && (
                <div className="muted" style={{ padding: 8 }}>Aucun résultat</div>
              )}
              {!loading && !!sProds.length && (
                <div style={{ padding: 6 }}>
                  <div style={{ fontSize: 12, color: '#64748b', margin: '2px 0 6px' }}>Produits</div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {sProds.map(p => (
                      <Link key={p.id} to={p.slug ? `/produits/${p.slug}` : '#'} onClick={() => setOpen(false)} style={{ textDecoration: 'none', color: '#111827', padding: '6px 8px', borderRadius: 8 }} className="hover">
                        {p.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {!loading && !!sProjs.length && (
                <div style={{ padding: 6 }}>
                  <div style={{ fontSize: 12, color: '#64748b', margin: '2px 0 6px' }}>Réalisations</div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {sProjs.map(p => (
                      <Link key={p.id} to={p.slug ? `/realisations/${p.slug}` : '#'} onClick={() => setOpen(false)} style={{ textDecoration: 'none', color: '#111827', padding: '6px 8px', borderRadius: 8 }} className="hover">
                        {p.title}{p.location ? ` — ${p.location}` : ''}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {!loading && (sProds.length + sProjs.length) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 6 }}>
                  <Link to={`/search?q=${encodeURIComponent((query || '').trim())}`} onClick={() => setOpen(false)} style={{ fontSize: 12 }}>Voir tous les résultats</Link>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="ctas">
          <NavLink className="btn ghost hide-sm" to="/contact" aria-label="Prendre rendez-vous">Prendre rendez-vous</NavLink>
          <NavLink className="btn filled hide-sm" to="/contact" aria-label="Devis gratuit">Devis gratuit</NavLink>
          <button
            type="button"
            aria-label="Ouvrir mes favoris"
            onClick={() => openFavoritesDrawer()}
            style={{ position: 'relative', border: '1px solid var(--border)', background: '#fff', borderRadius: 10, padding: '8px 10px', display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill={count > 0 ? '#ef4444' : 'none'} stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-8.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
            </svg>
            <span className="hide-sm" style={{ color: '#ef4444', fontWeight: 600 }}>Favoris</span>
            {count > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', fontSize: 11, borderRadius: 999, padding: '2px 6px' }}>{count}</span>
            )}
          </button>
          <AvatarMenu />
        </div>
      </div>
      <div className="container nav">
        <NavLink to="/" end>Accueil</NavLink>
        <NavLink to="/services">Services</NavLink>
        <NavLink to="/realisations">Réalisations</NavLink>
        <NavLink to="/catalogue">Catalogue</NavLink>
        <NavLink to="/agenda">Agenda</NavLink>
        <NavLink to="/qui-sommes-nous">Qui sommes-nous</NavLink>
        <NavLink to="/contact">Contact</NavLink>
        <div style={{ display: 'flex', gap: 12, marginLeft: 12, borderLeft: '1px solid #e5e7eb', paddingLeft: 16 }}>
          {fbUrl && (
            <a href={fbUrl} target="_blank" rel="noreferrer" style={{ color: '#64748b', transition: 'color .2s' }} onMouseEnter={e => e.currentTarget.style.color = '#1877f2'} onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
              <FacebookIcon size={20} />
            </a>
          )}
          {igUrl && (
            <a href={igUrl} target="_blank" rel="noreferrer" style={{ color: '#64748b', transition: 'color .2s' }} onMouseEnter={e => e.currentTarget.style.color = '#e4405f'} onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
              <InstagramIcon size={20} />
            </a>
          )}
          {lnUrl && (
            <a href={lnUrl} target="_blank" rel="noreferrer" style={{ color: '#64748b', transition: 'color .2s' }} onMouseEnter={e => e.currentTarget.style.color = '#0a66c2'} onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
              <LinkedinIcon size={20} />
            </a>
          )}
        </div>
      </div>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
